// The resources: charts and notes kept for reference rather than read in
// order, so listed by title, not date — and for the admin, their drafts above
// them and a way to start another. Each is a post like any other; only the
// list and the address differ.
import { useEffect, useMemo } from "react";
import { Link, useSearch } from "wouter";
import { Header } from "@/components/Header";
import { PageText } from "@/components/PageText";
import { Icon } from "@/components/Icon";
import { TagList } from "@/components/TagList";
import { useUser } from "@/lib/auth";
import { tagCounts, usePosts } from "@/lib/posts";

const label = "mb-2 font-sans text-[11px] tracking-[0.1em] text-ink-3 uppercase";

export function Resources() {
  const { data: user } = useUser();
  const isAdmin = user?.role === "admin";
  const { data: posts, isLoading, error } = usePosts("resources");
  const tag = new URLSearchParams(useSearch()).get("tag");

  useEffect(() => {
    document.title = "Resources · The Bible Unleashed";
  }, []);

  const tags = useMemo(() => tagCounts(posts ?? []), [posts]);
  // Drafts first, for the admin; then by title.
  const shown = useMemo(
    () =>
      (posts ?? [])
        .filter((p) => !tag || p.tags.includes(tag))
        .sort((a, b) => Number(a.status === "published") - Number(b.status === "published") || a.title.localeCompare(b.title)),
    [posts, tag],
  );

  return (
    <div className="min-h-screen">
      <Header />
      <main className="mx-auto max-w-[1080px] px-6 py-14 lg:px-10">
        <p className="text-xs tracking-[0.12em] text-ink-3 uppercase">Resources</p>
        <PageText
          id="resources"
          titleClassName="mt-1.5 font-serif text-[40px] font-semibold tracking-[-0.015em]"
          bodyClassName="mt-2 max-w-[560px] font-serif text-[17px] leading-normal"
        />

        <div className="mt-10 grid gap-16 lg:grid-cols-[minmax(0,1fr)_260px]">
          <div>
            {tag && (
              <p className="mb-4 font-sans text-[13px] text-ink-2">
                On “{tag}” ·{" "}
                <Link href="/resources" className="text-accent">
                  every resource
                </Link>
              </p>
            )}
            {isLoading && <p className="font-serif text-ink-3">Loading…</p>}
            {error && <p className="font-serif text-ink-2">Couldn’t load the resources: {error.message}</p>}
            {posts && shown.length === 0 && <p className="font-serif text-ink-3">Nothing published yet.</p>}

            {shown.map((p) => (
              <article key={p.id} className="border-t border-rule py-6 last:border-b">
                <h2 className="font-serif text-[24px] leading-[1.2] font-semibold tracking-[-0.01em]">
                  <Link href={`/resources/${p.slug}`} className="text-ink hover:text-accent">
                    {p.title}
                  </Link>
                  {p.status === "draft" && (
                    <span className="ml-2.5 rounded-full border border-rule px-2 py-0.5 align-middle font-sans text-[10.5px] font-normal tracking-[0.08em] text-ink-3 uppercase">
                      Draft
                    </span>
                  )}
                </h2>
                {p.summary && <p className="mt-2 font-serif text-[16.5px] leading-[1.55] text-ink-2">{p.summary}</p>}
                {p.tags.length > 0 && (
                  <div className="mt-2.5 flex flex-wrap gap-1.5">
                    {p.tags.map((t) => (
                      <Link
                        key={t}
                        href={`/resources?tag=${encodeURIComponent(t)}`}
                        className="rounded-full border border-rule px-2.5 py-0.5 font-sans text-[11.5px] text-ink-3 hover:text-ink"
                      >
                        {t}
                      </Link>
                    ))}
                  </div>
                )}
              </article>
            ))}
          </div>

          <aside className="space-y-8">
            {isAdmin && (
              <Link
                href="/resources/new"
                className="flex items-center justify-center gap-1.5 rounded-md bg-accent px-3 py-2 text-[13px] font-medium text-white"
              >
                <Icon name="plus" width="15" height="15" /> New resource
              </Link>
            )}
            <TagList title="Topics" tags={tags} current={tag} href={(t) => `/resources?tag=${encodeURIComponent(t)}`} />
            <div>
              <p className={label}>Follow along</p>
              <a href="/rss.xml" className="font-sans text-[13.5px] text-ink-2 hover:text-ink">
                RSS — new resources and chapters
              </a>
            </div>
          </aside>
        </div>
      </main>
    </div>
  );
}
